import { useState, type FormEvent } from 'react';
import { saveCategory } from '@/modules/inventory/services';
import { ActiveBadge } from '@/modules/inventory/components';
import { useCategories } from '@/modules/inventory/hooks';
import type { Category, CategoryInput } from '@/modules/inventory/types';
import { useAuth } from '@/shared/hooks/useAuth';
import {
  CATALOG_RULES_DEPLOY_HINT,
  shouldShowCatalogDeployHint,
} from '@/shared/utils/firestoreError';

const empty: CategoryInput = { name: '', description: '', active: true };

export function CategoriesPage() {
  const { categories, loading, error, refresh } = useCategories();
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('catalog.write');
  const showDeployHint = shouldShowCatalogDeployHint(profile);

  const [form, setForm] = useState<CategoryInput>(empty);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function startEdit(cat: Category) {
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      description: cat.description,
      active: cat.active,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(empty);
    setFormError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite) return;
    if (!form.name.trim()) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await saveCategory(form, editingId ?? undefined, profile);
      resetForm();
      await refresh();
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : 'No se pudo guardar. Verificá permisos y reglas de Firestore.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <h1>Categorías</h1>

      {canWrite ? (
        <form className="inventory-panel" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Editar categoría' : 'Nueva categoría'}</h2>
          <label className="auth-label">
            Nombre *
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Descripción
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <label className="auth-label auth-label--row">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Activa
          </label>
          {formError ? (
            <>
              <p className="auth-error">{formError}</p>
              {showDeployHint ? (
                <div
                  className="auth-muted"
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    border: '1px solid var(--border, #ccc)',
                    borderRadius: '4px',
                  }}
                >
                  <strong>Desbloquear catálogo (Fase 0)</strong>
                  <ol style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                    <li>
                      <code>corepack pnpm firebase:reauth</code> y{' '}
                      <code>corepack pnpm firestore:deploy</code>
                    </li>
                    <li>
                      Firebase Console → <code>users/{'{uid}'}</code>: role
                      superadmin/admin, active true, companyId default
                    </li>
                    <li>Recargar y volver a guardar la categoría</li>
                  </ol>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.9em' }}>
                    {CATALOG_RULES_DEPLOY_HINT}
                  </p>
                </div>
              ) : null}
            </>
          ) : null}
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            {editingId ? (
              <button
                type="button"
                className="auth-button auth-button--secondary"
                onClick={resetForm}
              >
                Cancelar
              </button>
            ) : null}
          </div>
        </form>
      ) : (
        <p className="auth-muted">Solo lectura — sin permiso de escritura.</p>
      )}

      <h2>Listado</h2>
      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}
      <table className="users-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Descripción</th>
            <th>Estado</th>
            {canWrite ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <tr key={cat.id}>
              <td>{cat.name}</td>
              <td>{cat.description || '—'}</td>
              <td>
                <ActiveBadge active={cat.active} />
              </td>
              {canWrite ? (
                <td>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => startEdit(cat)}
                  >
                    Editar
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
